import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import APIs from "../../Constants/apis";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import FormInput from "../../components/CustomComponents/Input/formInput";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";

const emptyForm = {
  id: 0,
  code: "NMI",
  name: "NMI",
  defaultHostURL: "https://secure.nmi.com",
  apiVersion: "v5",
  softwareHouseId: "",
  resellerId: "",
  refundsEnabled: true,
  active: true,
  sortOrder: 1,
};

const ManagePaymentGatewayPlatforms = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const isEdit = form.id > 0;

  const fetchPlatforms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.SA_GET_ALL_PAYMENT_GATEWAY_PLATFORMS, {
        params: {
          Search: search.trim() || "",
          SortBy: "SortOrder",
          SortOrder: "ASC",
        },
      });
      if (response.data.status) {
        setPlatforms(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        showErrorToast(response.data.message || "Failed to fetch payment gateway platforms");
        setPlatforms([]);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to fetch payment gateway platforms");
      setPlatforms([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchPlatforms();
  }, [fetchPlatforms]);

  const openAdd = () => {
    setForm({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (platform) => {
    setForm({
      id: platform.id,
      code: platform.code || "",
      name: platform.name || "",
      defaultHostURL: platform.defaultHostURL || "",
      apiVersion: platform.apiVersion || "",
      softwareHouseId: platform.softwareHouseId || "",
      resellerId: platform.resellerId || "",
      refundsEnabled: platform.refundsEnabled !== false,
      active: platform.active !== false,
      sortOrder: platform.sortOrder || 1,
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.code?.trim()) {
      showErrorToast("Code is required");
      return;
    }
    if (!form.name?.trim()) {
      showErrorToast("Name is required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ID: form.id || 0,
        Code: form.code.trim().toUpperCase(),
        Name: form.name.trim(),
        DefaultHostURL: form.defaultHostURL?.trim() || "",
        ApiVersion: form.apiVersion?.trim() || "",
        SoftwareHouseId: form.softwareHouseId?.trim() || "",
        ResellerId: form.resellerId?.trim() || "",
        RefundsEnabled: form.refundsEnabled,
        Active: form.active,
        SortOrder: parseInt(form.sortOrder, 10) || 1,
      };
      const response = await axiosInstance.post(APIs.SA_ADD_UPDATE_PAYMENT_GATEWAY_PLATFORM, payload);
      if (response.data.status) {
        showSuccessToast(response.data.message || "Platform saved");
        setShowModal(false);
        fetchPlatforms();
      } else {
        showErrorToast(response.data.message || "Failed to save platform");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save platform");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await swal({
      title: "Delete platform?",
      text: "Restaurants using this platform must be unlinked first.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.SA_DELETE_PAYMENT_GATEWAY_PLATFORM, {
        params: { ID: id },
      });
      if (response.data.status) {
        showSuccessToast("Platform deleted");
        fetchPlatforms();
      } else {
        showErrorToast(response.data.message || "Failed to delete platform");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to delete platform");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (platform) => {
    try {
      const response = await axiosInstance.patch(APIs.SA_UPDATE_PAYMENT_GATEWAY_PLATFORM_STATUS, null, {
        params: { ID: platform.id, Active: !platform.active },
      });
      if (response.data.status) {
        showSuccessToast("Status updated");
        fetchPlatforms();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
    } catch {
      showErrorToast("Failed to update status");
    }
  };

  const labelColor = isDarkMode ? "#ffffff" : "#000000";

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Payment Gateway Platforms</li>
          </ol>
          <h1 className="page-header mb-0">Payment Gateway Platforms</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={openAdd}
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Platform
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header d-flex align-items-center">
          <span className="fw-semibold">Platforms ({platforms.length})</span>
          <input
            className="form-control form-control-sm ms-auto"
            style={{ maxWidth: 260 }}
            placeholder="Search platforms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Host URL</th>
                  <th className="text-center">Refunds</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {platforms.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No platforms found. Add NMI or another gateway platform.
                    </td>
                  </tr>
                )}
                {platforms.map((platform) => (
                  <tr key={platform.id}>
                    <td>{platform.code}</td>
                    <td>{platform.name}</td>
                    <td>{platform.defaultHostURL || "—"}</td>
                    <td className="text-center">{platform.refundsEnabled !== false ? "On" : "Off"}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        className={`btn btn-sm ${platform.active ? "btn-success" : "btn-secondary"}`}
                        onClick={() => handleToggleStatus(platform)}
                      >
                        {platform.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => openEdit(platform)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(platform.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CustomModal
        title={isEdit ? "Update Platform" : "Add Platform"}
        subtitle="Platform settings apply to every restaurant that selects this gateway."
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSave}
        showButton={true}
        isCentered={true}
        modalSize="modal-lg"
        submitButtonText={isEdit ? "Update" : "Add"}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <FormInput
              label="Code"
              name="code"
              type="text"
              value={form.code}
              placeHolder="NMI"
              onchange={handleInputChange}
              readOnly={isEdit}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Name"
              name="name"
              type="text"
              value={form.name}
              placeHolder="NMI"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Default Host URL"
              name="defaultHostURL"
              type="text"
              value={form.defaultHostURL}
              placeHolder="https://secure.nmi.com"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="API Version"
              name="apiVersion"
              type="text"
              value={form.apiVersion}
              placeHolder="v5"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Software House ID"
              name="softwareHouseId"
              type="text"
              value={form.softwareHouseId}
              placeHolder="Optional / idle for NMI"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <FormInput
              label="Reseller ID"
              name="resellerId"
              type="text"
              value={form.resellerId}
              placeHolder="Optional / idle for NMI"
              onchange={handleInputChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={{ color: labelColor }}>
              Gateway refunds
            </label>
            <ToggleSwitch
              isChecked={form.refundsEnabled}
              onToggle={() => setForm((prev) => ({ ...prev, refundsEnabled: !prev.refundsEnabled }))}
              label={form.refundsEnabled ? "Enabled" : "Disabled"}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" style={{ color: labelColor }}>
              Status
            </label>
            <ToggleSwitch
              isChecked={form.active}
              onToggle={() => setForm((prev) => ({ ...prev, active: !prev.active }))}
              label={form.active ? "Active" : "Inactive"}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManagePaymentGatewayPlatforms;
